import { useApolloClient } from '@apollo/client/react';
import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { GalleryEditRequest } from '#components/document-viewer/gallery/gallery-config';
import { GalleryEditModal } from '#components/document-viewer/gallery/gallery-edit-modal';
import { GET_UPLOADS_FOR_DOCUMENT } from '#graphql/queries/document-uploads';

import { useDocumentContext } from '../document-context';

import { RenameDocumentModal } from './modals/rename-document-modal';
import { useRenameDocument } from './use-rename-document';
import { GridV1Editor, type GridV1EditorHandle } from './viewer/grid-v1-editor';
import { GridV1Toolbar, type GridV1EditorMode } from './viewer/grid-v1-toolbar';
import { MarkdownEditor } from './viewer/markdown-editor';

interface ViewDocumentViewerProps {
  apiHost: string;
  userEmail: string;
  onCreateSubpage: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  isRenameOpen: boolean;
  onRenameOpenChange: (open: boolean) => void;
}

export function ViewDocumentViewer({
  apiHost,
  userEmail,
  onCreateSubpage,
  onNavigateToDocument,
  isRenameOpen,
  onRenameOpenChange,
}: ViewDocumentViewerProps) {
  const {
    documentId,
    isNewDocument,
    isMarkdownDocument,
    isGridDocument,
    editorPlaceholderMuted,
    displayPath,
    markdownValue,
    setMarkdownValue,
    gridDocumentValue,
    setGridDocumentValue,
    childrenItems,
  } = useDocumentContext();
  const { documentPathForRename, getSerializedDocument, handleRename } =
    useRenameDocument();

  const openRename = useCallback(() => {
    onRenameOpenChange(true);
  }, [onRenameOpenChange]);

  const apolloClient = useApolloClient();
  const [galleryEditRequest, setGalleryEditRequest] =
    useState<GalleryEditRequest | null>(null);
  const [gridMode, setGridMode] = useState<GridV1EditorMode>('view');
  const gridEditorRef = useRef<GridV1EditorHandle | null>(null);

  useEffect(() => {
    setGridMode('view');
  }, [documentId]);

  const handleRequestGalleryEdit = useCallback(
    (request: GalleryEditRequest) => {
      setGalleryEditRequest(request);
    },
    [],
  );

  const handleUpload = useCallback(
    async (file: File): Promise<string> => {
      const { uploadImageFile } = await import('../upload-image');
      const result = await uploadImageFile(file, {
        apiHost,
        userEmail,
        ...(documentId ? { documentId } : {}),
      });
      return result.url;
    },
    [apiHost, documentId, userEmail],
  );

  const handleAttachUploadedRefetch = useCallback(() => {
    if (!documentId) return;

    void apolloClient.refetchQueries({ include: [GET_UPLOADS_FOR_DOCUMENT] });
  }, [apolloClient, documentId]);

  return (
    <>
      <GalleryEditModal
        request={galleryEditRequest}
        onDismiss={() => setGalleryEditRequest(null)}
        onUpload={handleUpload}
      />
      {isMarkdownDocument ? (
        <MarkdownEditor
          documentId={documentId}
          apiHost={apiHost}
          userEmail={userEmail}
          value={markdownValue}
          onChange={setMarkdownValue}
          parentPath={displayPath}
          children={childrenItems}
          editorPlaceholderMuted={editorPlaceholderMuted}
          isNewDocument={isNewDocument}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onRequestRename={openRename}
          onRequestGalleryEdit={handleRequestGalleryEdit}
          onUploaded={handleAttachUploadedRefetch}
        />
      ) : isGridDocument ? (
        <Box flex={1} minH={0} display="flex" flexDirection="column">
          <GridV1Toolbar
            mode={gridMode}
            onModeChange={setGridMode}
            onAddCell={() => gridEditorRef.current?.addCell()}
          />
          <GridV1Editor
            ref={gridEditorRef}
            mode={gridMode}
            value={gridDocumentValue}
            onChange={setGridDocumentValue}
            cellMarkdownEditorProps={{
              documentId,
              apiHost,
              userEmail,
              parentPath: displayPath,
              children: childrenItems,
              editorPlaceholderMuted,
              isNewDocument: false,
              onCreateSubpage,
              onNavigateToDocument,
              onRequestRename: openRename,
              onRequestGalleryEdit: handleRequestGalleryEdit,
              onUploaded: handleAttachUploadedRefetch,
            }}
          />
        </Box>
      ) : (
        <MarkdownEditor
          apiHost={apiHost}
          userEmail={userEmail}
          value={markdownValue}
          onChange={setMarkdownValue}
          parentPath={displayPath}
          children={childrenItems}
          editorPlaceholderMuted={editorPlaceholderMuted}
          isNewDocument={isNewDocument}
          documentId={documentId}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onRequestRename={openRename}
          onRequestGalleryEdit={handleRequestGalleryEdit}
          onUploaded={handleAttachUploadedRefetch}
        />
      )}

      {!isNewDocument && (
        <RenameDocumentModal
          open={isRenameOpen}
          onOpenChange={onRenameOpenChange}
          documentPathForRename={documentPathForRename}
          getCurrentValue={getSerializedDocument}
          onRename={handleRename}
        />
      )}
    </>
  );
}
